import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '../shared/layout/AppShell.js';
import { ComparisonPage } from '../features/dashboard/ComparisonPage.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { FeedbackPage } from '../features/feedback/FeedbackPage.js';
import { JourneyListPage } from '../features/journeys/JourneyListPage.js';
import { JourneyDetailPage } from '../features/journeys/JourneyDetailPage.js';
import { ProjectsPage } from '../features/projects/ProjectsPage.js';
import { RecommendationsPage } from '../features/recommendations/RecommendationsPage.js';
import { ScriptPage } from '../features/script-administration/ScriptPage.js';
import { LoginPage } from '../features/auth/LoginPage.js';
import { ProfilePage } from '../features/auth/ProfilePage.js';
import { SessionExpiredDialog } from '../features/auth/SessionExpiredDialog.js';
import { SetPasswordPage, type Purpose as LinkPurpose } from '../features/auth/SetPasswordPage.js';
import { AccountsPage } from '../features/accounts/AccountsPage.js';
import { useActiveProject } from '../shared/api/useActiveProject.js';
import { hasCentralAccess, restoreSession, useSession } from '../shared/auth/session.js';
import { useSessionGuard } from '../shared/auth/useSessionGuard.js';
import { PlaceholderPage } from './PlaceholderPage.js';

/** Pages reached from a link handed over by the central access, before any session exists. */
const LINK_PAGES = new Map<string, LinkPurpose>([
  ['/activation', 'activation'],
  ['/reset-password', 'password_reset'],
]);

export function App() {
  const session = useSession();
  const { pathname } = useLocation();

  useEffect(() => {
    void restoreSession();
  }, []);

  const linkPurpose = LINK_PAGES.get(pathname);
  if (linkPurpose) return <SetPasswordPage purpose={linkPurpose} />;
  if (session.status === 'unknown') return null;
  if (session.status === 'signed_out' || !session.account) return <LoginPage />;
  return (
    <>
      <AuthenticatedApp />
      {session.status === 'expired' && <SessionExpiredDialog email={session.account.email} />}
    </>
  );
}

function AuthenticatedApp() {
  useSessionGuard();
  const session = useSession();
  const { project } = useActiveProject();
  const { pathname } = useLocation();
  const central = hasCentralAccess(session);
  // The lineage is a canvas: it claims the viewport rather than the page scroll.
  const fullHeight = /^\/journeys\/[^/]+$/.test(pathname);

  return (
    <AppShell
      projectName={project?.name ?? 'Aucun projet'}
      userName={session.account?.displayName ?? ''}
      userRole={central ? 'Accès central' : 'Accès plateforme'}
      fullHeight={fullHeight}
    >
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        {/* Before/after comparison: a view of the dashboard zone (functional §5.1), not a menu entry. */}
        <Route path="/comparison" element={<ComparisonPage />} />
        <Route path="/journeys" element={<JourneyListPage />} />
        <Route path="/journeys/:journeyId" element={<JourneyDetailPage />} />
        <Route path="/projects" element={central ? <ProjectsPage /> : <Navigate to="/" replace />} />
        <Route path="/script" element={central ? <ScriptPage /> : <Navigate to="/" replace />} />
        <Route path="/recommendations" element={<RecommendationsPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/accounts" element={central ? <AccountsPage /> : <Navigate to="/" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/settings"
          element={
            <PlaceholderPage
              title="Paramètres"
              description="Seuils de déclenchement, indicateurs affichés, langues actives."
            />
          }
        />
        <Route
          path="/help"
          element={
            <PlaceholderPage
              title="Aide"
              description="Documentation fonctionnelle et technique du produit."
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
